import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { get } from 'lodash';
import usePermission from 'src/hooks/use-permission';
import { Redirect } from 'react-router-dom';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next'
import PageTitle from 'src/components/page-title';
import Button, { Type as ButtonType } from 'src/components/button';
import Divider from 'src/components/divider';
import CenterLoading from 'src/components/center-loading';
import Filters from 'src/pages/people/filters';
import DeallocationTable from 'src/pages/people/table';
import FormModal from 'src/pages/people/form-modal';
import FitModal from 'src/pages/people/fit-modal';
import CommentModal from 'src/pages/people/comment-modal';
import RollbackModal from 'src/pages/people/rollback-modal';
import MatchModal from 'src/pages/people/match-modal';
import DeleteModal from 'src/pages/people/delete-modal';
import FormInactivateModal from 'src/pages/people/form-inactivate-modal';
import ConfirmReactivateModal from 'src/pages/people/confirm-reactivate-modal';
import ETLUpdate from 'src/components/etl-update';
import { inactivateProfile, reactivateProfile } from 'src/apis/inactivation';
import './style.scss';
import Fuse from 'fuse.js';

/**
* Renders a deallocations component.
*
* @param {Object} props - The props for the Deallocations component.
* @param {Array} props.needs - The array of need objects.
* @param {Boolean} props.listFetching - Indicates if the deallocation list is being fetched.
* @param {Array} props.deallocations - The array of deallocation objects.
* @param {Object} props.user - The logged user object.
* @param {Function} props.getNeeds - The function to fetch the needs.
* @param {Object} props.positionMaps - The position maps object.
* @param {string} props.etlDate - The ETL date.
* @param {Function} props.filterDeallocations - The function to fetch the deallocations' data with or without filters.
* @param {Function} props.onCreateDeallocation - The callback function to handle the creation of a deallocation.
* @param {Function} props.onUpdateDeallocation - The callback function to handle the update of a deallocation.
* @param {Function} props.onDeleteDeallocation - The callback function to handle the deletion of a deallocation.
* @param {Function} props.onCreateFit - The callback function to handle the creation of a fit.
* @param {Function} props.onCancelFit - The callback function to handle the cancellation of a fit.
* @param {Function} props.onCreateMatch - The callback function to handle the creation of a match.
* @param {Function} props.onCancelMatch - The callback function to handle the cancellation of a match.
* @param {Object} props.filtersFromApi - The filters fetched from the API.
* @param {Array} props.profileOptions - The profile objects with value and label.
* @param {Function} props.onGetComments - The function to fetch comments.
* @param {Function} props.onCreateComment - The callback function to handle the creation of a comment.
* @param {Function} props.onEditComment - The callback function to handle the editing of a comment.
* @returns {JSX.Element} The rendered Deallocations component.
*/
function Deallocations({
  needs,
  listFetching,
  deallocations,
  user,
  getNeeds,
  positionMaps,
  etlDate,
  filterDeallocations,
  onCreateDeallocation,
  onUpdateDeallocation,
  onDeleteDeallocation,
  onCreateFit,
  onCancelFit,
  onCreateMatch,
  onCancelMatch,
  filtersFromApi,
  profileOptions,
  onGetComments,
  onCreateComment,
  onEditComment,
  onDeleteComment,
  totalCount = 0,
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  }
}) {
  const permissions = usePermission();
  const canCreate = get(permissions, 'deallocationPermissions.canCreate');
  const canSeeAll = get(permissions, 'deallocationPermissions.canSeeAll');
  const flagCreate = false;
  const canDelete = get(permissions, 'deallocationPermissions.canDeleteAll');
  const canEditDeallocation = get(permissions, 'deallocationPermissions.canEditAll');
  const canMatch = get(permissions, 'matchPermissions.canCreate');
  const canFit = get(permissions, 'fitPermissions.canCreate');
  const canFitPeople = get(permissions, 'fitPermissions.canSuggest');
  const [apiFilter, setApiFilter] = useState({});
  const [editingData, setEditingData] = useState(null);
  const [formFetching, setFormFetching] = useState(false);
  const [deleteFetching, setDeleteFetching] = useState(false);
  const [deletingDeallocation, setDeletingDeallocation] = useState(null);
  const [fitting, setFitting] = useState(null);
  const [matching, setMatching] = useState(null);
  const [commenting, setCommenting] = useState(null);
  const [confirmRollback, setConfirmRollback] = useState(null);
  const [inactivatingProfile, setInactivatingProfile] = useState(null);
  const [reactivatingProfile, setReactivatingProfile] = useState(null);
  const [inactivateFetching, setInactivateFetching] = useState(false);
  const [reactivateFetching, setReactivateFetching] = useState(false);
  const [filtersUpdated, setFiltersUpdated] = useState(false);
  const [currentPage, setCurrentPage] = useState(pagination.currentPage || 1);
  const [pageSize, setPageSize] = useState(pagination.limit || 10);
  
  const initialFetchDone = useRef(false);
  const isRequestPending = useRef(false);
  const isManualPageChange = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (pagination.currentPage && pagination.currentPage !== currentPage && !isManualPageChange.current) {
      setCurrentPage(pagination.currentPage);
    }
    isManualPageChange.current = false;
  }, [pagination.currentPage, currentPage]);

  const handleSetApiFilter = useCallback(
    (value) => {
      setApiFilter(value);
      const hasFilters = Object.values(value).some(val => {
        if (Array.isArray(val) && val.length > 0) {
          return true;
        }
        if (typeof val === 'string' && val.trim() !== '') {
          return true;
        }
        if (typeof val === 'number') {
          return true;
        }
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          return true;
        }
        return false;
      });
      if (hasFilters) {
        setFiltersUpdated(true);
      }
    }, [setApiFilter]);

  const handlePaginationChange = (page, size) => {
    if (isRequestPending.current) return;
    isRequestPending.current = true;
    isManualPageChange.current = true;
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
    const paginationOptions = {
      limit: size,
      currentPage: page,
      page: page
    };
    filterDeallocations(apiFilter, paginationOptions).finally(() => {
      isRequestPending.current = false;
    });
  };

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      return;
    }
    if (isRequestPending.current) return;
    isRequestPending.current = true;
    const hasFilters = Object.values(apiFilter).some(val => {
      if (Array.isArray(val) && val.length > 0) {
        return true;
      }
      if (typeof val === 'string' && val.trim() !== '') {
        return true;
      }
      if (typeof val === 'number') {
        return true;
      }
      if (val && typeof val === 'object' && Object.keys(val).length > 0) {
        return true;
      }
      return false;
    });
    if (hasFilters) {
      const paginationOptions = {
        limit: pageSize,
        currentPage: 1,
        page: 1
      };
      setCurrentPage(1);
      filterDeallocations(apiFilter, paginationOptions).finally(() => {
        isRequestPending.current = false;
      });
    } else if (!hasFilters && filtersUpdated) {
      const paginationOptions = {
        limit: pageSize,
        currentPage: 1,
        page: 1
      };
      setCurrentPage(1);
      filterDeallocations({}, paginationOptions).finally(() => {
        isRequestPending.current = false;
      });
      setFiltersUpdated(false);
    } else {
      isRequestPending.current = false;
    }
  }, [apiFilter, filterDeallocations, filtersUpdated, pageSize]);
  
  /**
   * Fetch deallocations' data and helper data.
   */
  const refreshDeallocations = useCallback(() => {
    if (isRequestPending.current) return;
    isRequestPending.current = true;
    getNeeds();
    const paginationOptions = {
      limit: pageSize,
      currentPage: currentPage,
      page: currentPage
    };
    filterDeallocations(apiFilter, paginationOptions).finally(() => {
      isRequestPending.current = false;
    });
    setFitting(null);
  }, [filterDeallocations, getNeeds, apiFilter, currentPage, pageSize]);

    
   /**
   * Call service layer function to create deallocation.
   *
   * @param {Object} deallocation - Deallocation object to be created.
   * @returns {Promise<void>}
   */
  const createDeallocation = async (deallocation) => {
    setFormFetching(true);
    try {
      await onCreateDeallocation(deallocation);
      notification.success({message: t("success.deallocations.create")});
    } catch (e) {
      setFormFetching(false);
      notification.error({message: t("errors.deallocations.create")});
      return;
    }
    setEditingData(null);
    setFormFetching(false);
  }

  /**
   * Call service layer function to edit deallocation.
   *
   * @param {Object} deallocation - Deallocation object to be updated.
   * @returns {Promise<void>}
   */
  const updateDeallocation = async (deallocation) => {
    setFormFetching(true);
    try {
      await onUpdateDeallocation(deallocation);
      notification.success({message: t("success.deallocations.edit")});
    } catch (e) {
      setFormFetching(false);
      notification.error({message: t("errors.deallocations.edit")});
      return;
    }
    setEditingData(null);
    setFormFetching(false);
  }

  /**
   * Call service layer function to delete deallocation.
   */
  const deleteDeallocation = async () => {
    setDeleteFetching(true);
    try {
      await onDeleteDeallocation(deletingDeallocation.id);
      setDeletingDeallocation(null);
      refreshDeallocations();
      notification.success({message: t("success.deallocations.delete")});
    } catch (e) {
      notification.error({message: t("errors.deallocations.delete")});
    }
    setDeleteFetching(false);
  };

  /**
   * Set deletingDeallocation value to null when cancelling delete action.
   *
   * @returns {void}
   */
  const onCancelDelete = () => {
    if (deleteFetching) {
      return;
    }
    setDeletingDeallocation(null);
  };

  /**
   * Call service layer function to create fit.
   *
   * @param {Array<string>} deallocationIds - Array with one string, which is the deallocationId
   * @param {Array<string>} needsId - Array with one string, which is the needId
   * @returns {void}
   */
  const createFit = async (deallocationIds, needsId) => {
    setFormFetching(true);
    try {
      await onCreateFit(deallocationIds, needsId);
      notification.success({message: t("success.fit.create")});
    } catch (e) {
      notification.error({message: t("errors.fit.create")});
      setFormFetching(false);
      return;
    }
    setFormFetching(false);
    setFitting(null);
    refreshDeallocations();
  };

  /**
   * Call service layer function to cancel fit.
   * @param {string} fitId - Text for fitId to be canceled.
   * @returns {void}
   */
  const cancelFit = async (fitId, cancelCategory, cancelSubcategory) => {
    setFormFetching(true);
    try {
      await onCancelFit(fitId, cancelCategory, cancelSubcategory);
      notification.success({message: t("success.fit.cancel")});
    } catch (e) {
      notification.error({message: t("errors.fit.cancel")});
      setFormFetching(false);
      return;
    }
    setFormFetching(false);
    setFitting(null);
    refreshDeallocations();
  };

  /**
   * Create a match based on the fit details provided.
   * Call service layer function to create match on a fit.
   * @param {string} fitId - Text for fitId to be matched.
   * @returns {void}
   */
  const createMatch = async (fitId, startDate, allocationType) => {
  setFormFetching(true);
  
  try {
    const rawResponse = await onCreateMatch(fitId, startDate, allocationType);
    const responseData = rawResponse?.data || rawResponse;
    const message = responseData?.message || responseData;
    
    if (!message) {
      notification.error({message: t(`errors.fit.general`)});
      return;
    }
    
    if (message.requisition === 'warn') {
      notification.error({message: t(`errors.fit.requisitionWarn`)});
      return;
    }
    
    if (message.match === 'ok') {
      notification.success({message: t(`success.fit.createMatch`)});
    } else if (message.match) {
      notification.error({message: t(`errors.fit.createMatch`)});
    }
    
    if (message.closeRequisition === 'ok') {
      notification.success({message: t(`success.fit.closeRequisition`)});
    } else if (message.closeRequisition === 'mock') {
      notification.success({message: t(`success.fit.closeRequisitionMock`)});
    } else if (message.closeRequisition) {
      notification.error({message: t(`errors.fit.closeRequisition`)});
    }
  } catch (e) {
    notification.error({message: t(`errors.fit.general`)});
  } finally {
    setFormFetching(false);
    setMatching(null);
    refreshDeallocations();
  }
};

  /**
   * Call function to create deallocation or update deallocation depending on editingData.id (if it exists, it means it's editing).
   * @param {Object} deallocation - Deallocation object from form.
   */
  const onSaveForm = async (deallocation) => {
    if (editingData.id) {
      await updateDeallocation(deallocation);
    } else {
      await createDeallocation(deallocation);
    }
    refreshDeallocations();
  };

  /**
   * Call service layer function to cancel/rollback match.
   * @param {string} deallocationId - Text for deallocationId.
   * @param {string} needId - Text for needId.
   * @param {string} [description] - (optional) text for description.
   * @returns {void}
   */
  const cancelMatch = async (deallocationId, needId, description, rollbackCategory, rollbackSubcategory) => {
    let matchRollback = false;
    let reopenedRequisition = false;
    setFormFetching(true);
    try {
      const {message} = (await onCancelMatch(deallocationId, needId, description, rollbackCategory, rollbackSubcategory)).data;
      matchRollback = message.matchRollback === 'ok';
      reopenedRequisition = message.reopenedRequisition;
    } catch (e) {
      setFormFetching(false);
      return;
    } finally {
      setFormFetching(false);
      setConfirmRollback(null);
      refreshDeallocations();
      matchRollback
        ? notification.success({message: t(`success.fit.rollbackMatch`)})
        : notification.error({message: t(`errors.fit.rollbackMatch`)});
      if (reopenedRequisition === 'ok') {
        notification.success({message: t(`success.fit.reopenRequisition`)});
      } else if (reopenedRequisition === 'mock') {
        notification.success({message: t(`success.fit.reopenRequisitionMock`)});
      } else {
        notification.error({message: t(`errors.fit.reopenRequisition`)});
      }
    }
  }

  const handleInactivateConfirm = async (data) => {
    setInactivateFetching(true);
    
    try {
      const deallocationId = data.collaboratorId;
      let reason = '';
      
      switch (data.reason) {
        case 'ON_NOTICE':
          reason = t('inactivateModal.reasons.onNotice');
          break;
        case 'CONTRACT_RENEWAL':
          reason = t('inactivateModal.reasons.contractRenewal');
          break;
        case 'LEAVE':
          reason = t('inactivateModal.reasons.leave');
          break;
        case 'OTHER':
          reason = data.otherReason;
          break;
        default:
          reason = data.reason;
      }

      const requestData = {
        reason: reason,
        inactiveDays: parseInt(data.days, 10)
      };

      await inactivateProfile(deallocationId, requestData);
      
      notification.success({
        message: t('success.deallocations.inactivate')
      });
      
      setInactivatingProfile(null);
      
      const paginationOptions = {
        limit: pageSize,
        currentPage: currentPage,
        page: currentPage
      };
      await filterDeallocations(apiFilter, paginationOptions);
      
    } catch (error) {
      console.error('Error inactivating profile:', error);
      
      let errorMessage = t('errors.deallocations.inactivate');
      
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;
        
        switch (status) {
          case 400:
            errorMessage = 'Dados inválidos para inativação.';
            break;
          case 401:
            errorMessage = 'Não autorizado. Verifique suas credenciais.';
            break;
          case 403:
            errorMessage = 'Você não tem permissão para inativar este perfil.';
            break;
          case 404:
            errorMessage = 'Perfil não encontrado.';
            break;
          case 409:
            errorMessage = 'Este perfil já está inativo ou possui conflitos.';
            break;
          case 500:
            errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
            break;
          default:
            errorMessage = responseData?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      notification.error({
        message: errorMessage,
        duration: 5
      });
    } finally {
      setInactivateFetching(false);
    }
  };

  const handleReactivateConfirm = async () => {
    setReactivateFetching(true);
    
    try {
      const deallocationId = reactivatingProfile.id;
      const collaboratorLogin = reactivatingProfile.collaborator_deallocation?.login;
      
      await reactivateProfile(deallocationId);
      
      notification.success({
        message: t('success.deallocations.reactivate')
      });
      
      setReactivatingProfile(null);
      
      const paginationOptions = {
        limit: pageSize,
        currentPage: currentPage,
        page: currentPage
      };
      await filterDeallocations(apiFilter, paginationOptions);
      
    } catch (error) {
      console.error('Error reactivating profile:', error);
      
      let errorMessage = t('errors.deallocations.reactivate');
      
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;
        
        switch (status) {
          case 400:
            errorMessage = 'Dados inválidos para reativação.';
            break;
          case 401:
            errorMessage = 'Não autorizado. Verifique suas credenciais.';
            break;
          case 403:
            errorMessage = 'Você não tem permissão para reativar este perfil.';
            break;
          case 404:
            errorMessage = 'Perfil não encontrado.';
            break;
          case 409:
            errorMessage = 'Este perfil já está ativo ou possui conflitos.';
            break;
          case 500:
            errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
            break;
          default:
            errorMessage = responseData?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      notification.error({
        message: errorMessage,
        duration: 5
      });
    } finally {
      setReactivateFetching(false);
    }
  };

  const canEdit =
    canEditDeallocation ||
    get(editingData, 'responsible_deallocation.email') === user.email;
  const isOwnerOfDeleting =
    get(deletingDeallocation, 'responsible_deallocation.email') === user.email;
  const effectiveTotalCount = pagination.totalCount || totalCount || deallocations.length;

  return (
    <div className='profile'>
      {canSeeAll === false && <Redirect to='/responsibles' />}
      {canSeeAll === false && notification.error({ message: t("errors.access") })}
      <div className="profile-header__container">
        <PageTitle title={t('peopleTitle')} />
        <ETLUpdate date={etlDate} />
      </div>
      <Divider />
      <div className='profile-table'>
        <div className='profile-table__header'>
          <h2 className='profile-table__header_counter'>
            {effectiveTotalCount === 1 || effectiveTotalCount === 0
              ? `${effectiveTotalCount} ${t('availablePeople')}`
              : `${effectiveTotalCount} ${t('availablePeoples')}`}
          </h2>
          <div className='profile-table__header_button'>
            {canCreate && flagCreate && (
              <Button
                className='create-deallocation-btn'
                type={ButtonType.Primary}
                uppercase
                onClick={() => setEditingData({})}
                id='add-people'
              >
                {t('addPeople')}
              </Button>
            )}
          </div>
        </div>
        <Filters
          onApiFilter={handleSetApiFilter}
          filters={filtersFromApi}
        />
        <div className='profile-table__container'>
          {!listFetching && (
            <DeallocationTable
              data={deallocations}
              user={user}
              canMatch={canMatch}
              canFit={canFit}
              canFitPeople={canFitPeople}
              canDelete={canDelete}
              onClickEdit={setEditingData}
              onClickDelete={setDeletingDeallocation}
              onClickFit={setFitting}
              onClickMatch={setMatching}
              onClickComment={setCommenting}
              onClickCancel={setConfirmRollback}
              onClickInactivate={setInactivatingProfile}
              onClickReactivate={setReactivatingProfile}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: pagination.totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
              }}
              onPaginationChange={handlePaginationChange}
              loading={listFetching}
            />
          )}
          {listFetching && <CenterLoading />}
        </div>
      </div>
      <DeleteModal
        loading={deleteFetching}
        people={deletingDeallocation}
        onConfirm={deleteDeallocation}
        isOwner={isOwnerOfDeleting}
        onCancel={onCancelDelete}
      />
      {editingData && (
        <FormModal
          canEdit={canEdit}
          user={user}
          loading={formFetching}
          onSave={onSaveForm}
          onClose={() => setEditingData(null)}
          deallocation={editingData}
          positionMaps={positionMaps}
        />
      )}
      {fitting ? (
        <FitModal
          loading={formFetching}
          deallocation={fitting}
          user={user}
          positionMaps={positionMaps}
          needs={needs}
          onSave={createFit}
          onCancelFit={cancelFit}
          onClose={() => setFitting(null)}
          deallocations={deallocations}
        />
      ) : null}
      {matching && (
        <MatchModal
          loading={formFetching}
          deallocation={matching}
          needs={needs}
          user={user}
          onSave={createMatch}
          onClose={() => setMatching(null)}
        />
      )}
      {confirmRollback && (
        <RollbackModal
          onConfirm={cancelMatch}
          onCancel={() => setConfirmRollback(null)}
          matchData={confirmRollback}
          deallocations={deallocations}
          loading={formFetching}
        />
      )}
      {commenting && (
        <CommentModal
          deallocation={commenting}
          onClose={() => setCommenting(null)}
          profileOptions={profileOptions}
          onGetComments={onGetComments}
          onCreateComment={onCreateComment}
          onEditComment={onEditComment}
          onDeleteComment={onDeleteComment}
        />
      )}
      {inactivatingProfile && (
        <FormInactivateModal
          visible={!!inactivatingProfile}
          collaborator={inactivatingProfile}
          loading={inactivateFetching}
          onCancel={() => setInactivatingProfile(null)}
          onConfirm={handleInactivateConfirm}
        />
      )}
      {reactivatingProfile && (
        <ConfirmReactivateModal
          visible={!!reactivatingProfile}
          collaborator={reactivatingProfile}
          loading={reactivateFetching}
          onCancel={() => setReactivatingProfile(null)}
          onConfirm={handleReactivateConfirm}
        />
      )}
    </div>
  );
}

export default Deallocations;